import { useState, useEffect } from 'react';
import { validateReservationForASP } from '../api/contractSettingsApi';
import { createASPFromReservation } from '../api/aspApi';
import { getDraftAcknowledgments, saveDraftAcknowledgments, deleteDraftAcknowledgments } from '../api/draftAcknowledgmentsApi';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import LawyerShareModal from './LawyerShareModal';
import AddressAutocomplete from '@/components/AddressAutocomplete';

const ACKNOWLEDGMENTS = [
  {
    id: 'not_legal_advice',
    label: 'Beaver is not providing legal advice',
    explanation: 'Beaver is a technology platform that helps manage paperwork. We cannot tell you what to sign or whether this is a good deal for you. Only a qualified lawyer can provide legal advice.'
  },
  {
    id: 'not_party',
    label: 'Beaver is not a party to this agreement',
    explanation: 'This contract is between you (the buyer) and the developer. Beaver simply provides the tools to create and sign the document - we have no involvement in the actual sale.'
  },
  {
    id: 'developer_terms',
    label: 'All terms are set by the Developer',
    explanation: 'The price, settlement dates, and all other conditions have been decided by the developer. Beaver has not negotiated anything on your behalf.'
  },
  {
    id: 'seek_legal_advice',
    label: 'I should seek independent legal advice',
    explanation: 'We strongly recommend you have a property lawyer review this agreement before signing. They can explain the legal implications and identify any concerns.'
  },
  {
    id: 'legally_binding',
    label: 'This becomes a legally binding contract once signed by both parties',
    explanation: 'Once you and the developer both sign, this agreement is legally enforceable. You will be committed to purchasing this property under these terms.'
  }
];

function ProgressIndicator({ currentStep, totalSteps }) {
  return (
    <div className="mb-6">
      <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
        <span>Step {currentStep} of {totalSteps}</span>
        <span>{Math.round((currentStep / totalSteps) * 100)}% complete</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
          style={{ width: `${(currentStep / totalSteps) * 100}%` }}
        />
      </div>
    </div>
  );
}

function AcknowledgmentItem({ item, checked, onChange, showExplanation, onToggleExplanation }) {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="flex items-start p-4 bg-white">
        <input
          type="checkbox"
          id={item.id}
          checked={checked}
          onChange={(e) => onChange(item.id, e.target.checked)}
          className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5 flex-shrink-0"
        />
        <div className="ml-3 flex-1">
          <label htmlFor={item.id} className="text-sm font-medium text-gray-900 cursor-pointer">
            {item.label}
          </label>
          <button
            type="button"
            onClick={() => onToggleExplanation(item.id)}
            className="ml-2 text-blue-600 hover:text-blue-800 text-sm underline"
          >
            {showExplanation ? 'Hide explanation' : 'What does this mean?'}
          </button>
        </div>
      </div>
      {showExplanation && (
        <div className="px-4 pb-4 pt-0 bg-blue-50 border-t border-blue-100">
          <p className="text-sm text-blue-800 ml-8">{item.explanation}</p>
        </div>
      )}
    </div>
  );
}

export default function ASPCreationWizard({ reservation, unit, project, buyer, onComplete }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingDraft, setLoadingDraft] = useState(true);
  const [savingDraft, setSavingDraft] = useState(false);
  const [error, setError] = useState(null);
  const [validationIssues, setValidationIssues] = useState([]);
  const [validation, setValidation] = useState(null);
  const [showLawyerModal, setShowLawyerModal] = useState(false);
  const [lawyerShared, setLawyerShared] = useState(false);
  const [expandedExplanations, setExpandedExplanations] = useState({});
  const [buyerAddress, setBuyerAddress] = useState(buyer?.address || '');

  const [acknowledgments, setAcknowledgments] = useState({
    not_legal_advice: false,
    not_party: false,
    developer_terms: false,
    seek_legal_advice: false,
    legally_binding: false
  });

  const hasAddressIssue = validationIssues.some(issue =>
    issue.toLowerCase().includes('address')
  );

  const allAcknowledged = Object.values(acknowledgments).every(Boolean);
  const acknowledgedCount = Object.values(acknowledgments).filter(Boolean).length;

  useEffect(() => {
    loadDraftAcknowledgments();
  }, [reservation.id]);

  async function loadDraftAcknowledgments() {
    try {
      setLoadingDraft(true);
      const draft = await getDraftAcknowledgments(reservation.id);
      if (draft?.acknowledgments) {
        setAcknowledgments(prev => ({
          ...prev,
          ...draft.acknowledgments
        }));
      }
    } catch (err) {
      console.error('Error loading draft:', err);
    } finally {
      setLoadingDraft(false);
    }
  }

  async function handleSaveDraft() {
    try {
      setSavingDraft(true);
      await saveDraftAcknowledgments(reservation.id, acknowledgments);
      setError(null);
    } catch (err) {
      console.error('Error saving draft:', err);
    } finally {
      setSavingDraft(false);
    }
  }

  async function handleAcknowledgmentChange(id, checked) {
    const newAcknowledgments = { ...acknowledgments, [id]: checked };
    setAcknowledgments(newAcknowledgments);

    try {
      await saveDraftAcknowledgments(reservation.id, newAcknowledgments);
    } catch (err) {
      console.error('Error auto-saving:', err);
    }
  }

  function toggleExplanation(id) {
    setExpandedExplanations(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  }

  async function handleStart() {
    try {
      setLoading(true);
      setError(null);
      setValidationIssues([]);
      const result = await validateReservationForASP(reservation.id);
      setValidation(result);
      if (result.valid) {
        setStep(2);
      } else {
        const issues = result.issues || [];
        setValidationIssues(issues);

        // Check if the only issue is the address
        const hasOnlyAddressIssue = issues.length > 0 && issues.every(issue =>
          issue.toLowerCase().includes('address')
        );

        // If the only issue is the address, go to address collection step
        if (hasOnlyAddressIssue) {
          setStep(1.5); // Address collection step
        } else {
          setError(result.message || 'Unable to proceed to contract');
        }
      }
    } catch (err) {
      console.error('Validation error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveAddress() {
    if (!buyerAddress || buyerAddress.trim() === '') {
      setError('Please enter your address');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Update the buyer's profile with the address
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ address: buyerAddress })
        .eq('id', buyer.id);

      if (updateError) throw updateError;

      // Re-validate now that address is provided
      const result = await validateReservationForASP(reservation.id);
      if (result.valid) {
        setStep(2);
      } else {
        setValidationIssues(result.issues || []);
        setError(result.message || 'Please resolve remaining issues');
      }
    } catch (err) {
      console.error('Error saving address:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    if (!allAcknowledged) {
      setError('Please acknowledge all items to proceed');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await createASPFromReservation(reservation.id, true);

      if (result.success) {
        await deleteDraftAcknowledgments(reservation.id);
        if (onComplete) onComplete(result);
        navigate(`/asp/${result.asp_id}`);
      } else {
        setError(result.message || 'Failed to create agreement');
      }
    } catch (err) {
      console.error('Creation error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleLawyerShareSuccess(result) {
    setLawyerShared(true);
    setShowLawyerModal(false);
  }

  if (step === 1) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <ProgressIndicator currentStep={1} totalSteps={3} />

        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Proceed to Agreement for Sale & Purchase
        </h2>

        <div className="space-y-4 mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-medium text-blue-900 mb-2">Your Reservation</h3>
            <div className="text-sm text-blue-800 space-y-1">
              <p><strong>Project:</strong> {project.name}</p>
              <p><strong>Unit:</strong> {unit.unit_number}</p>
              <p><strong>Price:</strong> ${unit.price?.toLocaleString()}</p>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">What happens next?</h3>
            <ol className="text-sm text-gray-700 space-y-2">
              <li className="flex items-start gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center font-medium">1</span>
                <span>We'll generate a Sale & Purchase Agreement with your details</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center font-medium">2</span>
                <span>You can review it and share it with your lawyer</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center font-medium">3</span>
                <span>When ready, you'll sign digitally, then the developer counter-signs</span>
              </li>
            </ol>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm text-green-800 font-medium">No commitment yet</p>
                <p className="text-sm text-green-700 mt-1">
                  Creating an agreement does not commit you to purchase. You can review it with your lawyer before signing.
                </p>
              </div>
            </div>
          </div>
        </div>

        {(error || validationIssues.length > 0) && !hasAddressIssue && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="font-medium text-red-800 mb-2">
              {error || 'Please resolve the following issues:'}
            </p>
            {validationIssues.length > 0 && (
              <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                {validationIssues.map((issue, idx) => (
                  <li key={idx}>{issue}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {hasAddressIssue && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-blue-800 font-medium mb-1">Address Required</p>
                <p className="text-sm text-blue-700">
                  We need your address for the Sale & Purchase Agreement. Don't worry - we'll collect it in the next step.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            onClick={handleStart}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
          >
            {loading ? 'Validating...' : 'Continue'}
          </button>
        </div>
      </div>
    );
  }

  // Address collection step (step 1.5)
  if (step === 1.5) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <ProgressIndicator currentStep={1} totalSteps={3} />

        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Provide Your Address
        </h2>

        <p className="text-gray-600 mb-6">
          Your address is required for the Sale & Purchase Agreement. This will be used as your legal address in the contract.
        </p>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-sm text-blue-800 font-medium mb-1">Privacy & Security</p>
              <p className="text-sm text-blue-700">
                Your address will only be shared with the developer and your lawyer (if applicable). It will appear in your Sale & Purchase Agreement.
              </p>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Your Address <span className="text-red-500">*</span>
          </label>
          <AddressAutocomplete
            value={buyerAddress}
            onChange={setBuyerAddress}
            placeholder="Start typing your address..."
          />
          <p className="text-xs text-gray-500 mt-1">
            Start typing and select your address from the dropdown
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="flex justify-between gap-3">
          <button
            onClick={() => setStep(1)}
            disabled={loading}
            className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 font-medium"
          >
            Back
          </button>
          <button
            onClick={handleSaveAddress}
            disabled={loading || !buyerAddress}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
          >
            {loading ? 'Saving...' : 'Continue'}
          </button>
        </div>
      </div>
    );
  }

  if (step === 2) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <ProgressIndicator currentStep={2} totalSteps={3} />

        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Understanding Your Agreement
          </h2>
          {savingDraft && (
            <span className="text-sm text-gray-500">Saving...</span>
          )}
        </div>

        <p className="text-gray-600 mb-6">
          Please read and acknowledge each point below. Click "What does this mean?" for a plain-English explanation.
        </p>

        <div className="space-y-3 mb-6">
          {ACKNOWLEDGMENTS.map(item => (
            <AcknowledgmentItem
              key={item.id}
              item={item}
              checked={acknowledgments[item.id]}
              onChange={handleAcknowledgmentChange}
              showExplanation={expandedExplanations[item.id]}
              onToggleExplanation={toggleExplanation}
            />
          ))}
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">
                {acknowledgedCount} of {ACKNOWLEDGMENTS.length} acknowledged
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Your progress is automatically saved
              </p>
            </div>
            <div className="w-32 bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(acknowledgedCount / ACKNOWLEDGMENTS.length) * 100}%` }}
              />
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
              </svg>
              <div>
                <p className="text-sm font-medium text-blue-900">Get legal advice</p>
                <p className="text-sm text-blue-800 mt-1">
                  We strongly recommend sharing this with your lawyer before proceeding.
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowLawyerModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium flex items-center gap-2 flex-shrink-0"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              Share with my lawyer
            </button>
          </div>
          {lawyerShared && (
            <div className="mt-3 pt-3 border-t border-blue-200 flex items-center gap-2 text-green-700">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-sm">Sent to your lawyer</span>
            </div>
          )}
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-gray-900 mb-2">Deposit Disclosure</h3>
          <p className="text-sm text-gray-700">
            Beaver does not collect or hold any deposits. All deposits are paid directly
            to the developer's lawyer's trust account as specified in the agreement.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <div className="flex justify-between gap-3">
          <button
            onClick={() => setStep(1)}
            disabled={loading}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Back
          </button>
          <div className="flex gap-3">
            <button
              onClick={handleSaveDraft}
              disabled={savingDraft}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm"
            >
              {savingDraft ? 'Saving...' : 'Save & Exit'}
            </button>
            <button
              onClick={() => setStep(3)}
              disabled={!allAcknowledged}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
            >
              Continue
            </button>
          </div>
        </div>

        {showLawyerModal && (
          <LawyerShareModal
            aspId={null}
            reservationId={reservation.id}
            preCreation={true}
            onClose={() => setShowLawyerModal(false)}
            onSuccess={handleLawyerShareSuccess}
          />
        )}
      </div>
    );
  }

  if (step === 3) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <ProgressIndicator currentStep={3} totalSteps={3} />

        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Confirm & Create Agreement
        </h2>

        <div className="space-y-4 mb-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-medium text-green-900">All acknowledgments confirmed</span>
            </div>
            <ul className="text-sm text-green-800 space-y-1 ml-7">
              {ACKNOWLEDGMENTS.map(item => (
                <li key={item.id} className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {item.label}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-medium text-blue-900 mb-2">Agreement Summary</h3>
            <div className="text-sm text-blue-800 space-y-1">
              <p><strong>Project:</strong> {project.name}</p>
              <p><strong>Unit:</strong> {unit.unit_number}</p>
              <p><strong>Purchase Price:</strong> ${unit.price?.toLocaleString()}</p>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-yellow-900">What happens now</p>
                <p className="text-sm text-yellow-800 mt-1">
                  Clicking "Create Agreement" will generate your Sale & Purchase Agreement.
                  You will still be able to review it and share it with your lawyer before signing.
                </p>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <div className="flex justify-between gap-3">
          <button
            onClick={() => setStep(2)}
            disabled={loading}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Back
          </button>
          <button
            onClick={handleCreate}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
          >
            {loading ? 'Creating Agreement...' : 'Create Agreement'}
          </button>
        </div>
      </div>
    );
  }

  return null;
}
